import Joi from '@hapi/joi';
import { captureMessage } from '@sentry/browser';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Batch } from '../../../../common/src/domain/batch/batchTableSchema';
import { BatchStates } from '../../../../common/src/enums/BatchStates';
import { InspectionStandardTypes } from '../../../../common/src/enums/InspectionStandardTypes';
import { Roles } from '../../../../common/src/enums/Roles';
import { SurveyStates } from '../../../../common/src/enums/SurveyStates';
import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { ILanguageCodeProps, IValidationComponentState, ValidatingComponent } from '../../mixins/validation/validation';
import { fetchGet, fetchPost } from '../../store/fetch';
import { IConnectProps } from '../../utils/IConnectProps';
import { IRouterProps } from '../../utils/IRouterProps';
import {
    Alert,
    Content,
    EditPanelButtons,
    EditPanelCell,
    EditPanelDatePicker,
    EditPanelExplanation,
    EditPanelFilterCreateSelect,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelSwitch,
    PanelBody,
    PanelHeader,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeftOnly,
    Tray,
} from '../elements';

type BatchView = Pick<Batch, 'id' | 'inspectionStandardType' | 'name'>;

interface ISurveyAddEditProps extends ITranslateProps, IConnectProps, ILanguageCodeProps, IRouterProps {
    userRole: Roles;
    useNen2767Advanced: boolean;
    useBatches: boolean;
    isClient: boolean;
    useProductTitle: boolean;
}

interface ISurveyAddEditState extends IValidationComponentState {
    currentLanguage?: string;
    surveryedOn?: Date;
    status?: string;
    inspectionStandardType?: string;
    objectInspectionStandardType?: string;
    description?: string;
    objectId?: string;
    operatorCompanyId?: string;
    surveyorCompanyId?: string;
    ownerCompanyId?: string;
    objectName?: string;
    objectCompositionIsVisible?: boolean;
    isFmecaAvailable: boolean;
    isCraAvailable: boolean;
    batches: BatchView[];
    areThereActiveBatches: boolean;
    batchId?: string;
    referenceNumber?: string;
    knownLabels: string[];
    labels: string[];
    reportDeadlineOnDate: string | null;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        languageCode: language.code,
        useBatches: authentication.useBatches,
        isClient: authentication.isClient,
        userRole: authentication.role,
        useNen2767Advanced: authentication.useNen2767Advanced,
        useProductTitle: authentication.useProductTitle,
    };
}

class SurveyAddEdit extends ValidatingComponent<ISurveyAddEditProps, ISurveyAddEditState> {
    constructor(props: ISurveyAddEditProps) {
        super(props);

        this.state = {
            surveryedOn: new Date(),
            reportDeadlineOnDate: moment().add(14, 'days').toISOString(),
            description: '',
            submitting: false,
            failed: false,
            saved: false,
            objectId: this.props.location.query.objectId,
            isFmecaAvailable: true,
            isCraAvailable: false,
            batches: [],
            areThereActiveBatches: true,
            errors: {},
            labels: [],
            knownLabels: [],
        };
    }

    public override async componentDidMount() {
        if (this.props.match.params.id) {
            await this.load();
            if (this.state.objectId) {
                await this.loadObject(this.state.objectId);
            }
        } else {
            await this.loadObject(this.props.location.query.objectId);
        }
        if (this.props.useBatches) {
            await this.loadBatches();
        }
        await this.loadKnownLabels();
    }

    public getValidatorTypes(): any {
        return {
            inspectionStandardType:
                this.props.useNen2767Advanced &&
                ((this.props.useBatches && (this.props.userRole === Roles.Administrator || this.props.isClient)) || !this.props.useBatches)
                    ? Joi.required()
                    : Joi.allow(null, ''),
        };
    }

    public getValidatorData() {
        return {
            surveryedOn: this.state.surveryedOn,
            status: this.state.status,
            inspectionStandardType: this.state.inspectionStandardType,
            description: this.state.description,
            objectId: this.state.objectId,
            operatorCompanyId: this.state.operatorCompanyId,
            surveyorCompanyId: this.state.surveyorCompanyId,
            isFmecaAvailable: this.state.inspectionStandardType === InspectionStandardTypes.Fmeca ? this.state.isFmecaAvailable : false,
            isCraAvailable: this.state.inspectionStandardType === InspectionStandardTypes.Fmeca ? this.state.isCraAvailable : false,
            batchId: this.state.batchId,
            referenceNumber: this.state.referenceNumber,
            labels: this.state.labels,
            reportDeadlineOnDate: this.state.reportDeadlineOnDate,
        };
    }

    public override render() {
        return (
            <div>
                <TopbarLeftOnly>
                    <TopbarCrumbActive icon="fa-camera" title={this.state.objectName} />
                    <TopbarCrumb>
                        <Link to={`/surveys/${this.props.match.params.id}`}>{this.props.t('common.surveyDetails')}</Link>
                    </TopbarCrumb>
                    <TopbarCrumb>
                        <Link to={`/objects/${this.state.objectId}`}>{this.props.t('common.objectDetails')}</Link>
                    </TopbarCrumb>
                    <TopbarCrumb>
                        <Link to="/objects">{this.props.t('common.objects')}</Link>
                    </TopbarCrumb>
                </TopbarLeftOnly>

                <Content>
                    <Tray>
                        <div className="admin-form theme-primary mw800 center-block">
                            {!this.state.areThereActiveBatches && this.props.useBatches && (
                                <Alert severity="info">{this.props.t('batch.noActiveBatchesForObject')}</Alert>
                            )}
                            <div className="panel">
                                <form method="post" action="/" id="admin-form" onSubmit={(e) => this.saveClicked(e)}>
                                    <PanelBody>
                                        <EditPanelSection>
                                            <EditPanelInput
                                                name="description"
                                                placeholder={
                                                    this.props.useProductTitle
                                                        ? this.props.t('product.description')
                                                        : this.props.t('survey.description')
                                                }
                                                value={this.state.description}
                                                icon="fa fa-tag"
                                                handleChange={async (value) => this.handleChange(value, 'description')}
                                            />
                                        </EditPanelSection>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSelect
                                                    name="status"
                                                    value={this.state.status}
                                                    topLabel={this.props.t('common.status')}
                                                    handleChange={async (value) => this.handleChange(value, 'status')}
                                                    options={this.surveyStates()}
                                                />
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelInput
                                                    name="referenceNumber"
                                                    value={this.state.referenceNumber}
                                                    placeholder={this.props.t('survey.referenceNumber')}
                                                    topLabel={this.props.t('survey.referenceNumber')}
                                                    handleChange={async (value) => this.handleChange(value, 'referenceNumber')}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={3}>
                                                <EditPanelDatePicker
                                                    name="surveryedOn"
                                                    value={this.state.surveryedOn ?? null}
                                                    handleChange={async (value) => this.handleChange(value, 'surveryedOn')}
                                                    topLabel={this.props.t('survey.surveyedOn')}
                                                    placeholder={this.props.t('common.date')}
                                                />
                                                {this.renderHelpText(this.getValidationMessages('surveryedOn'))}
                                            </EditPanelCell>

                                            <EditPanelCell rowCount={3}></EditPanelCell>

                                            <EditPanelCell rowCount={3}>
                                                <EditPanelDatePicker
                                                    name="reportDeadlineOnDate"
                                                    value={this.state.reportDeadlineOnDate}
                                                    handleChange={this.handleChange.bind(this)}
                                                    topLabel={this.props.t('survey.reportDeadlineOnDate')}
                                                    placeholder={this.props.t('common.date')}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        {this.props.useNen2767Advanced && (
                                            <>
                                                {this.props.useBatches && (
                                                    <>
                                                        {((this.props.userRole === Roles.Administrator || this.props.isClient) && (
                                                            <>
                                                                {this.state.batches.length !== 0 && (
                                                                    <>
                                                                        <PanelHeader text={this.props.t('batch.batch')} />
                                                                        <EditPanelRow>
                                                                            <EditPanelCell rowCount={12}>
                                                                                <EditPanelSelect
                                                                                    name="batchId"
                                                                                    value={this.state.batchId}
                                                                                    handleChange={async (value) =>
                                                                                        this.handleChange(value, 'batchId')
                                                                                    }
                                                                                    options={[
                                                                                        { value: '', text: this.props.t('batch.notSelected') },
                                                                                        ...new Set(
                                                                                            this.state.batches.map((i) => ({
                                                                                                value: i.id,
                                                                                                text: `${i.name} (${this.props.t(
                                                                                                    `inspectionStandard.${i.inspectionStandardType}`,
                                                                                                )})`,
                                                                                            })),
                                                                                        ),
                                                                                    ]}
                                                                                />
                                                                            </EditPanelCell>
                                                                        </EditPanelRow>
                                                                    </>
                                                                )}
                                                                <PanelHeader text={this.props.t('common.inspectionStandard')} />
                                                                <EditPanelRow>
                                                                    <EditPanelCell rowCount={6}>
                                                                        <EditPanelSelect
                                                                            name="inspectionStandardType"
                                                                            value={this.state.inspectionStandardType}
                                                                            handleChange={async (value) =>
                                                                                this.handleChange(value, 'inspectionStandardType')
                                                                            }
                                                                            placeholder={this.props.t(
                                                                                'common.selectItem',
                                                                                this.props.t('common.inspectionStandard'),
                                                                            )}
                                                                            notValid={!this.isValid('inspectionStandardType')}
                                                                            options={getInspectionStandardTypeOptions(this.props.t)}
                                                                        />
                                                                    </EditPanelCell>
                                                                </EditPanelRow>
                                                            </>
                                                        )) || (
                                                            <>
                                                                {this.state.batches.length !== 0 && (
                                                                    <>
                                                                        <PanelHeader text={this.props.t('batch.batch')} />
                                                                        <EditPanelRow>
                                                                            <EditPanelCell rowCount={12}>
                                                                                {(this.state.batches.length === 1 && (
                                                                                    <>
                                                                                        {this.state.batches[0]!.name} (
                                                                                        {this.props.t(
                                                                                            `inspectionStandard.${
                                                                                                this.state.batches[0]!.inspectionStandardType
                                                                                            }`,
                                                                                        )}
                                                                                        )
                                                                                    </>
                                                                                )) || (
                                                                                    <>
                                                                                        <EditPanelSelect
                                                                                            name="batchId"
                                                                                            value={this.state.batchId}
                                                                                            handleChange={async (value) =>
                                                                                                this.handleChange(value, 'batchId')
                                                                                            }
                                                                                            options={this.state.batches.map((i) => ({
                                                                                                value: i.id,
                                                                                                text: `${i.name} (${this.props.t(
                                                                                                    `inspectionStandard.${i.inspectionStandardType}`,
                                                                                                )})`,
                                                                                            }))}
                                                                                        />
                                                                                    </>
                                                                                )}
                                                                            </EditPanelCell>
                                                                        </EditPanelRow>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                                {!this.props.useBatches && (
                                                    <>
                                                        <PanelHeader text={this.props.t('common.inspectionStandard')} />
                                                        <EditPanelRow>
                                                            <EditPanelCell rowCount={6}>
                                                                <EditPanelSelect
                                                                    name="inspectionStandardType"
                                                                    value={this.state.inspectionStandardType}
                                                                    handleChange={async (value) => this.handleChange(value, 'inspectionStandardType')}
                                                                    placeholder={this.props.t(
                                                                        'common.selectItem',
                                                                        this.props.t('common.inspectionStandard'),
                                                                    )}
                                                                    notValid={!this.isValid('inspectionStandardType')}
                                                                    options={getInspectionStandardTypeOptions(this.props.t)}
                                                                />
                                                                {this.renderHelpText(this.getValidationMessages('inspectionStandardType'))}
                                                            </EditPanelCell>
                                                        </EditPanelRow>
                                                    </>
                                                )}
                                                {this.state.inspectionStandardType === InspectionStandardTypes.Fmeca && (
                                                    <>
                                                        <PanelHeader text={this.props.t('common.settings')} />
                                                        <EditPanelSection>
                                                            <EditPanelSwitch
                                                                name="isFmecaAvailable"
                                                                placeholder={this.props.t('survey.isFmecaAvailable')}
                                                                value={this.state.isFmecaAvailable}
                                                                handleChange={async (value) => this.handleChange(value, 'isFmecaAvailable')}
                                                            />
                                                        </EditPanelSection>
                                                        <EditPanelSection>
                                                            <EditPanelSwitch
                                                                name="isCraAvailable"
                                                                placeholder={this.props.t('survey.isCraAvailable')}
                                                                value={this.state.isCraAvailable}
                                                                handleChange={async (value) => this.handleChange(value, 'isCraAvailable')}
                                                            />
                                                        </EditPanelSection>
                                                    </>
                                                )}
                                            </>
                                        )}

                                        <PanelHeader text={this.props.t('common.labels')} />
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={12}>
                                                <EditPanelFilterCreateSelect
                                                    name="labels"
                                                    placeholder={this.props.t('common.selectItem', this.props.t('common.labels'))}
                                                    value={this.state.labels}
                                                    options={this.state.knownLabels}
                                                    handleChange={this.handleChange.bind(this)}
                                                />
                                                <div className="mt15">
                                                    <EditPanelExplanation text={this.props.t('survey.labelsExpl')} />
                                                </div>
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    </PanelBody>

                                    <EditPanelButtons footer>
                                        <button
                                            type="button"
                                            disabled={this.state.submitting}
                                            className="button mr10"
                                            onClick={() => this.props.navigate(-1)}
                                        >
                                            {' '}
                                            {this.props.t('common.cancel')}{' '}
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={
                                                this.state.submitting ||
                                                (!this.state.areThereActiveBatches &&
                                                    !this.props.isClient &&
                                                    (this.props.userRole === Roles.Surveyor || this.props.userRole === Roles.Operator))
                                            }
                                            className={this.props.match.params.id ? 'button btn-primary' : 'button mr10'}
                                        >
                                            {' '}
                                            {this.props.t('common.save')}
                                        </button>
                                        {!this.props.match.params.id && (
                                            <button
                                                type="button"
                                                disabled={
                                                    this.state.submitting ||
                                                    (!this.state.areThereActiveBatches &&
                                                        !this.props.isClient &&
                                                        (this.props.userRole === Roles.Surveyor || this.props.userRole === Roles.Operator))
                                                }
                                                onClick={(e) => this.saveClicked(e, true)}
                                                className="button btn-primary"
                                            >
                                                {' '}
                                                {this.props.t('survey.saveSurveyAndAddVisuals')}{' '}
                                                <span className="fa fa-angle-double-right pl5"></span>
                                            </button>
                                        )}
                                    </EditPanelButtons>
                                </form>
                            </div>
                        </div>
                    </Tray>
                </Content>
            </div>
        );
    }

    protected override async onChanged(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;

        if (stateKey === 'isFmecaAvailable' && value === false) {
            newState.isCraAvailable = true;
        }
        if (stateKey === 'isCraAvailable' && value === false) {
            newState.isFmecaAvailable = true;
        }
        if (stateKey === 'batchId') {
            const inspectionStandardType = this.state.batches.find((i) => i.id === value)?.inspectionStandardType;
            if (inspectionStandardType) {
                newState.inspectionStandardType = inspectionStandardType;
            }
        }
        if (stateKey === 'surveryedOn' && !this.props.match.params.id) {
            newState.reportDeadlineOnDate = moment(value).add(14, 'days').toISOString();
        }

        await this.setStatePromised(newState);
    }

    private async loadBatches() {
        const response = await fetchGet('/api/batcheslist', { objectId: this.state.objectId, status: BatchStates.Active });
        if (response.json.items.length > 0) {
            await this.setStatePromised({
                batches: response.json.items,
                areThereActiveBatches: true,
                batchId: response.json.items[0].id,
                inspectionStandardType: response.json.items[0].inspectionStandardType,
            });
        } else {
            await this.setStatePromised({ areThereActiveBatches: false });
        }
    }

    private async load() {
        const url = `/api/surveys/${this.props.match.params.id}`;
        const result = await fetchGet(url);
        result.json.surveryedOn = new Date(result.json.surveryedOn);
        await this.setStatePromised(result.json);
    }

    private async loadObject(objectId: string) {
        const url = `/api/objects/${objectId}?fields=name,inspection,inspectionStandardType`;
        const result = await fetchGet(url);
        result.json.surveryedOn = new Date(result.json.surveryedOn);
        await this.setStatePromised({ objectName: result.json.name, objectInspectionStandardType: result.json.inspectionStandardType });
        if (result.json.inspectionStandardType === InspectionStandardTypes.Nen2767 && !this.state.inspectionStandardType) {
            await this.setStatePromised({ inspectionStandardType: InspectionStandardTypes.Nen2767 });
        }
    }

    private surveyStates() {
        const options = [
            { value: SurveyStates.Open, text: this.props.t(`surveyStatus.${SurveyStates.Open}`) },
            { value: SurveyStates.Preparation, text: this.props.t(`surveyStatus.${SurveyStates.Preparation}`) },
            { value: SurveyStates.Conducting, text: this.props.t(`surveyStatus.${SurveyStates.Conducting}`) },
            { value: SurveyStates.Reporting, text: this.props.t(`surveyStatus.${SurveyStates.Reporting}`) },
        ];

        if ([Roles.Administrator].includes(this.props.userRole) || this.props.isClient) {
            options.push({ value: SurveyStates.Delivery, text: this.props.t(`surveyStatus.${SurveyStates.Delivery}`) });
            options.push({ value: SurveyStates.Completed, text: this.props.t(`surveyStatus.${SurveyStates.Completed}`) });
        }
        // administrator can edit survey to status deleted
        if (this.props.userRole === Roles.Administrator && this.props.match.params.id) {
            options.push({ value: SurveyStates.Deleted, text: this.props.t(`surveyStatus.${SurveyStates.Deleted}`) });
        }
        return options;
    }

    private saveClicked(e: any, shouldGoToEdit?: boolean) {
        e.preventDefault();

        captureMessage('Survey save clicked', { extra: { objectId: this.state.objectId, batchId: this.state.batchId } });

        this.validate(async (validationError: any) => {
            if (!validationError) {
                let url = '/api/surveys';
                if (this.props.match.params.id) {
                    url += `/${this.props.match.params.id}`;
                }
                const data = this.getValidatorData() as any;

                captureMessage('Post new survey', { extra: { objectId: this.state.objectId, batchId: this.state.batchId } });
                const result = await fetchPost(url, {}, data);

                if (shouldGoToEdit) {
                    if (this.state.inspectionStandardType === InspectionStandardTypes.Fmeca) {
                        this.props.navigate(`/surveys/${result.json.id}/analysis`);
                    } else if (this.state.inspectionStandardType === InspectionStandardTypes.Memo) {
                        this.props.navigate(`/surveys/${result.json.id}/summaryAndAdvice`);
                    } else if (this.state.inspectionStandardType === InspectionStandardTypes.Model3d) {
                        this.props.navigate(`/surveys/${result.json.id}/pointcloud`);
                    } else {
                        this.props.navigate(`/surveys/${result.json.id}/editReport`);
                    }
                } else {
                    this.props.navigate(-1);
                }
            }
        });
    }

    private async loadKnownLabels() {
        const url = '/api/surveys/knownLabels';
        const result = await fetchGet(url);
        await this.setStatePromised({ knownLabels: result?.json });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(
    connect<Record<string, unknown>, Record<string, unknown>, ISurveyAddEditProps>(mapStateToProps, undefined, undefined, { forwardRef: true })(
        SurveyAddEdit as any,
    ) as any,
);
